import { useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Container from '@mui/material/Container';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import { styled, useTheme, alpha } from "@mui/material/styles";
import RateReviewOutlinedIcon from '@mui/icons-material/RateReviewOutlined';
import SendRoundedIcon from '@mui/icons-material/SendRounded'; // Rounded icon
import CircularProgress from '@mui/material/CircularProgress'; // For loading state

// Import the sendFeedback function
import { sendFeedback } from '../../utils/fastAPI';

import colorConfigs from '../../configs/colorConfigs'; // Adjust path if necessary

// Styled Button for a modern, clean look
const StyledButton = styled(Button)(({ theme }) => ({
    color: colorConfigs.button?.text || theme.palette.common.white,
    backgroundColor: colorConfigs.primary || theme.palette.primary.main, // Same color as icon
    padding: theme.spacing(1.25, 3.5),
    fontSize: '0.9375rem', // 15px
    fontWeight: 600,
    textTransform: 'none',
    borderRadius: '12px', // Generous, modern border radius
    boxShadow: `0 4px 15px ${alpha(colorConfigs.primary || theme.palette.primary.main, 0.2)}`,
    transition: 'background-color 0.2s ease-out, box-shadow 0.2s ease-out, transform 0.15s ease-out',
    '&:hover': {
        backgroundColor: alpha(colorConfigs.primary || theme.palette.primary.main, 0.9),
        boxShadow: `0 6px 20px ${alpha(colorConfigs.primary || theme.palette.primary.main, 0.3)}`,
        transform: 'translateY(-1px)',
    },
    '&:active': {
        transform: 'translateY(0px)',
        backgroundColor: alpha(colorConfigs.primary || theme.palette.primary.main, 0.7), // Darker shade when active
        boxShadow: `0 3px 10px ${alpha(colorConfigs.primary || theme.palette.primary.main, 0.1)}`,
    },
    '&:disabled': {
        opacity: 0.6, // Use opacity instead of alpha for a simpler approach
        backgroundColor: colorConfigs.primary || theme.palette.primary.main, 
        color: theme.palette.common.white,
        boxShadow: 'none',
    }
}));

// Styled TextField for enhanced readability and aesthetics
const StyledTextField = styled(TextField)(({ theme }) => ({
    '& .MuiInputLabel-root': {
        color: colorConfigs.text?.secondary || theme.palette.text.secondary,
        fontWeight: 500,
        fontSize: '1rem',
    },
    '& .MuiInputLabel-root.Mui-focused': {
        color: colorConfigs.primary || theme.palette.primary.main,
    },
    '& .MuiOutlinedInput-root': {
        backgroundColor: colorConfigs.tertiary || alpha(theme.palette.grey[500], 0.05),
        borderRadius: '12px', // Match button's modern radius
        transition: theme.transitions.create(['border-color', 'box-shadow', 'background-color']),
        color: colorConfigs.text?.primary || theme.palette.text.primary,
        '& fieldset': {
            borderColor: colorConfigs.border?.input || theme.palette.grey[300],
            borderWidth: '1px',
        },
        '&:hover fieldset': {
            borderColor: alpha(colorConfigs.primary || theme.palette.primary.main, 0.7),
        },
        '&.Mui-focused': {
            backgroundColor: theme.palette.background.paper, // Or a slightly different shade on focus
            '& fieldset': {
                borderColor: colorConfigs.border?.inputFocus || theme.palette.primary.main,
                borderWidth: '1.5px', // Slightly thicker on focus
            },
            // Optional: subtle glow on focus
            boxShadow: `0 0 0 3px ${alpha(colorConfigs.primary || theme.palette.primary.main, 0.1)}`,
        },
        '& .MuiInputBase-input': {
            padding: theme.spacing(2, 1.75), // Comfortable padding (16px 14px)
            fontSize: '1rem', // 16px
            fontWeight: 400,
            '&::placeholder': {
                color: colorConfigs.text?.secondary || theme.palette.text.secondary,
                opacity: 0.8,
            },
        },
    },
}));

const FeedbackPage = () => {
    const theme = useTheme();
    const [feedback, setFeedback] = useState("");
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState("");
    const [snackbarSeverity, setSnackbarSeverity] = useState("success");
    const [loading, setLoading] = useState(false); // Add loading state

    const handleInputChange = (event) => {
        setFeedback(event.target.value);
    };

    const handleSendFeedback = async () => {
        if (feedback.trim() === "") {
            setSnackbarMessage("Your feedback is valuable, please write something!");
            setSnackbarSeverity("warning");
            setSnackbarOpen(true);
            return;
        }

        setLoading(true); // Start loading

        try {
            // Send feedback via backend API
            const result = await sendFeedback(feedback);
            
            if (result.success) {
                setSnackbarMessage("Thank you for your feedback! We've received it.");
                setSnackbarSeverity("success");
                setFeedback("");
            } else {
                throw new Error(result.message || "Failed to send feedback");
            }
        } catch (error) {
            console.error('Error sending feedback:', error);
            setSnackbarMessage("There was an issue sending your feedback. Please try again.");
            setSnackbarSeverity("error");
        } finally {
            setLoading(false); // End loading
            setSnackbarOpen(true);
        }
    };

    const handleCloseSnackbar = (event, reason) => {
        if (reason === 'clickaway') {
            return;
        }
        setSnackbarOpen(false);
    };

    return (
        <Box
            sx={{
                minHeight: '100vh',
                width: '100%', // Ensure full width
                backgroundColor: colorConfigs.background?.default || theme.palette.background.default,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'flex-start', // Align to top, let content flow
                pt: { xs: 6, md: 10 }, // More padding at the top
                pb: { xs: 4, md: 6 },
                px: { xs: 2, sm: 3 }, // Horizontal padding for the page
                margin: 0, // Remove any margin
                boxSizing: 'border-box', // Include padding in width calculation
            }}
        >
            <Container maxWidth="sm"> {/* Max width 'sm' for a more focused form area */}
                <Box sx={{ textAlign: 'center', mb: { xs: 4, md: 5 } }}>
                    <RateReviewOutlinedIcon
                        sx={{
                            fontSize: { xs: '3.5rem', md: '4.5rem' },
                            color: colorConfigs.primary || theme.palette.primary.main,
                            mb: 1.5,
                        }}
                    />
                    <Typography
                        variant="h2" // More impactful title
                        component="h1"
                        sx={{
                            fontWeight: 700,
                            color: colorConfigs.text?.primary || theme.palette.text.primary,
                            lineHeight: 1.25,
                            mb: 1.5,
                            fontSize: { xs: '1.75rem', sm: '2rem', md: '2.25rem' }, // Responsive font size
                        }}
                    >
                        Share Your Thoughts
                    </Typography>
                    <Typography
                        variant="body1" // Standard body text for readability
                        sx={{
                            fontSize: '1.125rem', // 18px
                            color: colorConfigs.text?.secondary || theme.palette.text.secondary,
                            maxWidth: '45ch',
                            margin: '0 auto',
                            lineHeight: 1.7,
                        }}
                    >
                        We're always looking to improve. Tell us how we can make your experience better.
                    </Typography>
                </Box>

                <Box
                    component="form"
                    noValidate
                    autoComplete="off"
                    onSubmit={(e) => { e.preventDefault(); handleSendFeedback(); }}
                    sx={{ width: '100%' }}
                >
                    <StyledTextField
                        id="feedback-input"
                        label="Your Feedback"
                        multiline
                        minRows={7}
                        maxRows={15}
                        fullWidth
                        value={feedback}
                        onChange={handleInputChange}
                        placeholder="What's on your mind? (e.g., feature requests, bugs, compliments)"
                        sx={{ mb: 3 }}
                    />

                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                        <StyledButton
                            type="submit"
                            variant='contained'
                            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SendRoundedIcon />}
                            disabled={!feedback.trim() || loading}
                            size="large" // MUI size prop
                        >
                            {loading ? 'Sending...' : 'Send Feedback'}
                        </StyledButton>
                    </Box>
                </Box>
            </Container>

            <Snackbar
                open={snackbarOpen}
                autoHideDuration={6000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                sx={{ bottom: { xs: 20, sm: 30 } }} // Adjust position if needed
            >
                <Alert
                    onClose={handleCloseSnackbar}
                    severity={snackbarSeverity}
                    variant="filled"
                    elevation={6} // Add elevation to snackbar
                    sx={{ width: '100%', fontSize: '1rem', alignItems: 'center' }}
                >
                    {snackbarMessage}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default FeedbackPage;